import {
  Checkbox,
  CloseButton,
  Combobox,
  InputBase,
  ScrollArea,
  useCombobox,
} from '@mantine/core'
import React, { useMemo, useState } from 'react'

import { TextInput } from '@components/mantine/text-input.component'

type MultiSelectItem = {
  value: string
  label: string
  disabled?: boolean
}

type RenderTriggerProps = {
  values: string[]
  selectedLabels: string[]
  toggle: () => void
  isOpen: boolean
  disabled: boolean
  onRemove: (value: string) => void
}

type MultiSelectProps = {
  data: MultiSelectItem[]
  value?: string[]
  onChange?: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
  searchable?: boolean
  clearable?: boolean
  fullWidth?: boolean
  className?: string
  nothingFoundMessage?: string
  maxSelectedValues?: number
  renderTrigger?: (props: RenderTriggerProps) => React.ReactNode
}

export function MultiSelect({
  data,
  value = [],
  onChange,
  placeholder = 'Select...',
  disabled = false,
  searchable = false,
  clearable = false,
  fullWidth = false,
  className = '',
  nothingFoundMessage = 'No options found',
  maxSelectedValues,
  renderTrigger,
}: MultiSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setSearch('')
    },
    onDropdownOpen: () => {
      if (searchable) {
        combobox.focusSearchInput()
      }
    },
  })

  const [search, setSearch] = useState('')

  // Get selected labels for display
  const selectedLabels = useMemo(() => {
    return value
      .map(v => data.find(item => item.value === v)?.label)
      .filter((label): label is string => label !== undefined)
  }, [data, value])

  // Display text for trigger
  const displayText = useMemo(() => {
    if (value.length === 0) return null
    if (value.length === 1) return selectedLabels[0]
    return `${value.length} Selected`
  }, [value, selectedLabels])

  // Filter items based on search
  const filteredData = useMemo(() => {
    if (!search) return data

    const searchLower = search.toLowerCase().trim()
    return data.filter(item => item.label.toLowerCase().includes(searchLower))
  }, [data, search])

  const hasOptions = filteredData.length > 0

  const handleValueToggle = (itemValue: string) => {
    const isSelected = value.includes(itemValue)

    if (isSelected) {
      // Remove item
      onChange?.(value.filter(v => v !== itemValue))
    } else {
      // Add item (check max limit)
      if (maxSelectedValues && value.length >= maxSelectedValues) {
        return
      }
      onChange?.([...value, itemValue])
    }
  }

  const handleRemove = (itemValue: string) => {
    onChange?.(value.filter(v => v !== itemValue))
  }

  const options = filteredData.map(item => {
    const isSelected = value.includes(item.value)
    const isDisabled = item.disabled && !isSelected

    return (
      <Combobox.Option
        key={item.value}
        value={item.value}
        disabled={isDisabled}
        className="px-4 py-3 rounded-md cursor-pointer text-xl! data-[combobox-selected=true]:bg-grey-50 data-[combobox-active=true]:bg-grey-100"
      >
        <div className="flex items-center justify-between w-full">
          <span>{item.label}</span>
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            readOnly
            tabIndex={-1}
            className="pointer-events-none"
          />
        </div>
      </Combobox.Option>
    )
  })

  return (
    <Combobox
      store={combobox}
      withinPortal
      portalProps={{ target: '#select-portal' }}
      position="bottom-start"
      offset={4}
      middlewares={{ flip: true, shift: true }}
      onOptionSubmit={val => {
        handleValueToggle(val)
        // Don't close dropdown for multi-select
      }}
    >
      <Combobox.Target>
        {renderTrigger ? (
          renderTrigger({
            values: value,
            selectedLabels,
            toggle: () => combobox.toggleDropdown(),
            isOpen: combobox.dropdownOpened,
            disabled,
            onRemove: handleRemove,
          })
        ) : (
          <InputBase
            component="button"
            type="button"
            pointer
            rightSection={
              clearable && value.length > 0 ? (
                <CloseButton
                  size="sm"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => onChange?.([])}
                  aria-label="Clear selection"
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            rightSectionPointerEvents={
              clearable && value.length > 0 ? 'all' : 'none'
            }
            onClick={() => combobox.toggleDropdown()}
            disabled={disabled}
            size="lg"
            classNames={{
              input: `form-input text-xl ${fullWidth ? 'w-full' : ''} ${className}`,
              wrapper: 'bg-transparent',
            }}
          >
            {displayText || (
              <span className="text-grey-400 text-xl">{placeholder}</span>
            )}
          </InputBase>
        )}
      </Combobox.Target>

      <Combobox.Dropdown className="bg-white shadow-lg rounded-lg border border-grey-100 p-2">
        {searchable && (
          <div className="p-2">
            <TextInput
              value={search}
              onChange={setSearch}
              placeholder="Search..."
              autoFocus
            />
          </div>
        )}
        <ScrollArea.Autosize mah={300}>
          <Combobox.Options>
            {hasOptions ? (
              options
            ) : (
              <Combobox.Empty className="text-center py-4 text-grey-400">
                {nothingFoundMessage}
              </Combobox.Empty>
            )}
          </Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export type { MultiSelectItem, RenderTriggerProps as MultiSelectRenderTriggerProps }
